import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, ArrowUpRight, ArrowDownRight, Filter, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { SortHeader, useSort } from "@/components/SortHeader";
import { toast } from "sonner";
import ExportButtons from "@/components/ExportButtons";

const INCOME_CATS = ["Satış", "Danışmanlık", "Faiz Geliri", "Kira Geliri", "Tahsilat", "Diğer Gelir"];
const EXPENSE_CATS = ["Kira", "Maaş", "Elektrik & Su", "Yazılım Aboneliği", "Pazarlama", "Ofis Malzeme", "Vergi", "Ulaşım", "Ödeme", "Diğer Gider"];

const LIMIT = 25;

export default function Transactions() {
  const [txs, setTxs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("bank");
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [txStart, setTxStart] = useState("");
  const [txEnd, setTxEnd] = useState("");
  const [form, setForm] = useState({
    type: "income",
    amount: "",
    category: "",
    description: "",
    account_id: "",
    date: new Date().toISOString().slice(0, 10),
  });

  const load = async () => {
    const params = { skip: page * LIMIT, limit: LIMIT, source };
    if (filter !== "all") params.type = filter;
    if (search.trim()) params.search = search.trim();
    if (txStart) params.start = txStart;
    if (txEnd) params.end = txEnd;
    const [t, a] = await Promise.all([api.transactions(params), api.accounts()]);
    setTxs(t.items);
    setTotal(t.total);
    setAccounts(a);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter, search, source, txStart, txEnd]);

  const changeFilter = (f) => {
    setFilter(f);
    setPage(0);
  };
  const changeSource = (s) => {
    setSource(s);
    setPage(0);
    setFilter("all");
  };

  const submit = async () => {
    if (!form.amount || !form.category) {
      toast.error("Tutar ve kategori zorunludur");
      return;
    }
    await api.createTransaction({
      ...form,
      amount: parseFloat(form.amount),
      date: new Date(form.date).toISOString(),
      account_id: form.account_id || null,
      source: "bank",
    });
    toast.success("İşlem eklendi");
    setOpen(false);
    setForm({ type: "income", amount: "", category: "", description: "", account_id: "", date: new Date().toISOString().slice(0, 10) });
    load();
  };

  const remove = async (id) => {
    await api.deleteTransaction(id);
    toast.success("İşlem silindi");
    load();
  };

  const cats = form.type === "income" ? INCOME_CATS : EXPENSE_CATS;
  const accName = (id) => accounts.find((a) => a.id === id)?.name || "-";
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const txSort = useSort(txs, {
    description: (t) => t.description || t.category || "",
    category: (t) => t.category || "",
    account: (t) => accName(t.account_id),
    date: (t) => t.date || "",
    amount: (t) => (t.type === "income" ? 1 : -1) * (t.amount || 0),
  });
  const sortedTxs = txSort.sorted;

  const exportParams = { source };
  if (filter !== "all") exportParams.type = filter;
  if (search.trim()) exportParams.search = search.trim();
  if (txStart) exportParams.start = txStart;
  if (txEnd) exportParams.end = txEnd;

  return (
    <div className="p-5 pt-20 lg:p-10 lg:pt-10">
      <PageHeader
        title="Gelir / Gider"
        subtitle={source === "bank" ? `${total.toLocaleString("tr-TR")} banka hareketi` : `${total.toLocaleString("tr-TR")} fatura`}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <ExportButtons path="/export/transactions" params={exportParams} label="Hareket listesi" testidPrefix="tx-export" />
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-transaction-button" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="h-4 w-4" /> <span className="ml-1.5">Yeni İşlem</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card">
              <DialogHeader>
                <DialogTitle className="font-display">Yeni İşlem Ekle</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="tx-type-income"
                    onClick={() => setForm({ ...form, type: "income", category: "" })}
                    className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${form.type === "income" ? "border-emerald-500 bg-emerald-500/15 text-emerald-400" : "border-border text-muted-foreground"}`}
                  >
                    Gelir
                  </button>
                  <button
                    data-testid="tx-type-expense"
                    onClick={() => setForm({ ...form, type: "expense", category: "" })}
                    className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${form.type === "expense" ? "border-rose-500 bg-rose-500/15 text-rose-400" : "border-border text-muted-foreground"}`}
                  >
                    Gider
                  </button>
                </div>
                <div>
                  <Label>Tutar (lei)</Label>
                  <Input
                    data-testid="tx-amount-input"
                    type="number"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0,00"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Kategori</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="tx-category-select" className="mt-1.5">
                      <SelectValue placeholder="Kategori seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {cats.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Banka Hesabı</Label>
                  <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                    <SelectTrigger data-testid="tx-account-select" className="mt-1.5">
                      <SelectValue placeholder="Hesap seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tarih</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>Açıklama</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={2} />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="tx-submit-button" onClick={submit} className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="mb-5 inline-flex rounded-lg border border-border bg-card p-1">
        {[["bank", "Banka Hareketleri"], ["invoice", "Faturalar"]].map(([k, l]) => (
          <button
            key={k}
            data-testid={`source-tab-${k}`}
            onClick={() => changeSource(k)}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${source === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          {["all", "income", "expense"].map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => changeFilter(f)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${filter === f ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
            >
              {f === "all" ? "Tümü" : f === "income" ? "Gelir" : "Gider"}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="transaction-search-input"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Fatura no, cari veya kategori ara..."
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-1.5" title="Tarih aralığı (listeyi filtreler ve dışa aktarır)">
            <Input data-testid="tx-start-date" type="date" value={txStart} onChange={(e) => { setTxStart(e.target.value); setPage(0); }} className="h-9 w-[140px]" />
            <span className="text-muted-foreground">–</span>
            <Input data-testid="tx-end-date" type="date" value={txEnd} onChange={(e) => { setTxEnd(e.target.value); setPage(0); }} className="h-9 w-[140px]" />
            {(txStart || txEnd) && (
              <button data-testid="tx-date-clear" onClick={() => { setTxStart(""); setTxEnd(""); setPage(0); }} className="rounded-lg px-2 py-1 text-xs text-muted-foreground hover:text-foreground">Temizle</button>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <SortHeader label="İşlem" sortId="description" sort={txSort} />
              <SortHeader label="Kategori" sortId="category" sort={txSort} className="hidden sm:table-cell" />
              <SortHeader label="Hesap" sortId="account" sort={txSort} className="hidden md:table-cell" />
              <SortHeader label="Tarih" sortId="date" sort={txSort} className="hidden sm:table-cell" />
              <SortHeader label="Tutar" sortId="amount" sort={txSort} align="right" />
              <th className="px-5 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {txs.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-muted-foreground">Kayıt bulunamadı</td></tr>
            )}
            {sortedTxs.map((t) => {
              const inc = t.type === "income";
              return (
                <tr key={t.id} data-testid="transaction-row" className="border-b border-border/50 transition-colors hover:bg-secondary/30">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${inc ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
                        {inc ? <ArrowDownRight className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                      </div>
                      <div>
                        <div className="font-medium">{t.description || t.category}</div>
                        <div className="text-xs text-muted-foreground sm:hidden">{t.category} · {fmtDate(t.date)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="hidden px-5 py-3.5 text-muted-foreground sm:table-cell">{t.category}</td>
                  <td className="hidden px-5 py-3.5 text-muted-foreground md:table-cell">{accName(t.account_id)}</td>
                  <td className="hidden px-5 py-3.5 text-muted-foreground sm:table-cell">{fmtDate(t.date)}</td>
                  <td className={`px-5 py-3.5 text-right font-mono font-semibold tabular ${inc ? "text-emerald-400" : "text-rose-400"}`}>
                    {inc ? "+" : "-"}{money(t.amount, t.currency)}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <button data-testid="delete-transaction-button" onClick={() => remove(t.id)} className="text-muted-foreground transition-colors hover:text-rose-400">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          Sayfa {page + 1} / {totalPages}
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="prev-page-button"
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" /> Önceki
          </Button>
          <Button
            data-testid="next-page-button"
            variant="outline"
            size="sm"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Sonraki <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
